import React from "react";
import { ExperimentOutlined, PlusOutlined, RocketOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Progress,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Typography,
  message,
} from "antd";
import { isAxiosError } from "axios";
import type { ColumnsType } from "antd/es/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  attachSamplePack,
  createComplianceCase,
  downloadCompliancePassport,
  downloadPortfolioReport,
  getComplianceDashboardStats,
  listComplianceCases,
  sendPortfolioDigest,
} from "../api";
import { countryLabel, ensureCountriesLoaded } from "../countries/countryUtils";
import type { ComplianceCaseSummary, ComplianceDashboardStats } from "./types";
import { OnboardingTour } from "./components/OnboardingTour";
import { MobileDataCards } from "./components/MobileDataCards";
import { TIMBER_DE_DEMO_PRESET } from "./ux/demoPreset";
import { readinessPercentFromStatus } from "./ux/caseReadiness";
import { pushNotification } from "./ux/notificationStore";
import { useNarrowViewport } from "./ux/useNarrowViewport";
import { InvestorDemoModal } from "../investor/InvestorDemoModal";
import { CaseStatusBadge } from "./components/StatusBadge";

export function CaseListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useNarrowViewport("md");
  const [cases, setCases] = React.useState<ComplianceCaseSummary[]>([]);
  const [stats, setStats] = React.useState<ComplianceDashboardStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [demoLoading, setDemoLoading] = React.useState(false);
  const [investorOpen, setInvestorOpen] = React.useState(false);
  const [clientOrgFilter, setClientOrgFilter] = React.useState("");
  const [, setCountriesReady] = React.useState(false);

  React.useEffect(() => {
    ensureCountriesLoaded()
      .then(() => setCountriesReady(true))
      .catch(() => setCountriesReady(true));
  }, []);

  React.useEffect(() => {
    if (searchParams.get("investorDemo") === "1") {
      setInvestorOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("investorDemo");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [caseList, dash] = await Promise.all([
        listComplianceCases(clientOrgFilter || undefined),
        getComplianceDashboardStats(),
      ]);
      setCases(caseList);
      setStats(dash);
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          setLoadError(t("compliance.caseList.signInRequired"));
        } else if (status === 502 || status === 503) {
          setLoadError(t("compliance.caseList.serviceUnavailable"));
        } else {
          setLoadError(err.response?.data?.detail ?? t("compliance.caseList.loadFailed"));
        }
      } else {
        setLoadError(t("compliance.caseList.loadFailed"));
      }
      setCases([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [t, clientOrgFilter]);

  React.useEffect(() => {
    load();
  }, [load]);

  const runDemoPreset = async () => {
    setDemoLoading(true);
    try {
      const created = await createComplianceCase(TIMBER_DE_DEMO_PRESET);
      try {
        await attachSamplePack(created.id);
      } catch {
        // Sample pack optional — case still usable.
      }
      message.success(t("compliance.demoPreset.created"));
      pushNotification({
        kind: "passport_generated",
        title: t("compliance.demoPreset.created"),
        caseId: created.id,
      });
      navigate(`/dds/cases/${created.id}`);
    } catch {
      message.error(t("compliance.demoPreset.failed"));
    } finally {
      setDemoLoading(false);
    }
  };

  const columns: ColumnsType<ComplianceCaseSummary> = [
    {
      title: t("compliance.columns.supplier"),
      dataIndex: "supplier_name",
      key: "supplier_name",
    },
    {
      title: t("compliance.columns.clientOrg"),
      dataIndex: "client_org_name",
      key: "client_org_name",
      render: (v: string | null) => v || t("common.dash"),
      responsive: ["md"],
    },
    {
      title: t("compliance.columns.commodity"),
      dataIndex: "commodity",
      key: "commodity",
      render: (v: string) => v.replace("_", " "),
      responsive: ["sm"],
    },
    {
      title: t("compliance.columns.destination"),
      dataIndex: "destination_country",
      key: "destination_country",
      render: (code: string) => countryLabel(code, i18n.language) || code,
      responsive: ["sm"],
    },
    {
      title: t("compliance.columns.status"),
      dataIndex: "status",
      key: "status",
      render: (s: string) => <CaseStatusBadge status={s} />,
    },
    {
      title: t("compliance.readiness.short"),
      key: "readiness",
      width: 110,
      render: (_, row) => (
        <Progress
          percent={readinessPercentFromStatus(row.status)}
          size="small"
          showInfo={false}
          status={row.status === "shared" ? "success" : "active"}
        />
      ),
      responsive: ["md"],
    },
    {
      title: t("compliance.columns.risk"),
      dataIndex: "risk_score",
      key: "risk_score",
      render: (v: number | null) => (v != null ? `${v.toFixed(0)}` : t("common.dash")),
      responsive: ["lg"],
    },
    {
      title: t("compliance.columns.actions"),
      key: "actions",
      render: (_, row) => (
        <Space wrap>
          <Button type="link" onClick={() => navigate(`/dds/cases/${row.id}`)}>
            {t("common.open")}
          </Button>
          {(row.status === "passport_ready" || row.status === "shared") && (
            <Button
              type="link"
              loading={downloadingId === row.id}
              disabled={downloadingId !== null && downloadingId !== row.id}
              onClick={async () => {
                setDownloadingId(row.id);
                try {
                  await downloadCompliancePassport(row.id);
                } catch {
                  message.error(t("compliance.passport.downloadFailed"));
                } finally {
                  setDownloadingId(null);
                }
              }}
            >
              {t("common.download")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const onDownloadReport = async (format: "csv" | "html") => {
    try {
      await downloadPortfolioReport(format, clientOrgFilter || undefined);
      message.success(t("compliance.report.downloaded"));
    } catch {
      message.error(t("compliance.report.failed"));
    }
  };

  const onEmailDigest = async () => {
    try {
      await sendPortfolioDigest(clientOrgFilter || undefined);
      message.success(t("compliance.report.digestQueued"));
    } catch {
      message.error(t("compliance.report.digestFailed"));
    }
  };

  const demoButtons = (
    <Space wrap className={isMobile ? "case-list-toolbar--mobile" : undefined} size="small">
      <Button
        icon={<RocketOutlined />}
        onClick={() => setInvestorOpen(true)}
        data-testid="investor-demo-button"
      >
        {t("investor.demo.button")}
      </Button>
      <Button
        icon={<ExperimentOutlined />}
        loading={demoLoading}
        onClick={runDemoPreset}
        data-testid="demo-preset-button"
      >
        {t("compliance.demoPreset.button")}
      </Button>
      <Button data-testid="portfolio-report-csv" onClick={() => void onDownloadReport("csv")}>
        {t("compliance.report.downloadCsv")}
      </Button>
      <Button data-testid="portfolio-report-html" onClick={() => void onDownloadReport("html")}>
        {t("compliance.report.downloadHtml")}
      </Button>
      <Button data-testid="portfolio-digest" onClick={() => void onEmailDigest()}>
        {t("compliance.report.emailDigest")}
      </Button>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        data-tour="dds-new-case"
        className="dds-touch-target"
        onClick={() => navigate("/dds/new")}
      >
        {t("compliance.caseList.newCase")}
      </Button>
    </Space>
  );

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large" data-tour="dds-case-list">
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <OnboardingTour autoStart />
      </div>
      {loading && !stats ? (
        <Skeleton active paragraph={{ rows: 4 }} data-testid="case-list-skeleton" />
      ) : (
        stats && (
          <div className="impact-panel" data-testid="impact-panel">
            <Typography.Title level={4} style={{ marginTop: 0 }}>
              {t("investor.impact.title")}
            </Typography.Title>
            {stats.total_cases === 0 && (
              <Typography.Paragraph type="secondary">{t("investor.impact.emptyHint")}</Typography.Paragraph>
            )}
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title={t("investor.impact.cases")} value={stats.total_cases} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title={t("investor.impact.passports")} value={stats.passports_issued ?? 0} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title={t("investor.impact.avgRisk")}
                    value={stats.avg_risk_score ?? 0}
                    precision={1}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title={t("investor.impact.timeToPassport")}
                    value={stats.avg_hours_to_passport ?? 0}
                    precision={2}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title={t("compliance.dashboard.passportReady")}
                    value={(stats.by_status.passport_ready || 0) + (stats.by_status.shared || 0)}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title={t("compliance.dashboard.riskHigh")} value={stats.risk_buckets.high || 0} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title={t("compliance.dashboard.issues")} value={stats.by_status.issues_found || 0} />
                </Card>
              </Col>
            </Row>
          </div>
        )
      )}
      <Card title={t("compliance.caseList.title")} extra={demoButtons}>
        {loadError && (
          <Alert
            type="error"
            message={loadError}
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={load}>
                {t("common.retry")}
              </Button>
            }
          />
        )}
        <Input.Search
          allowClear
          placeholder={t("compliance.caseList.filterClientOrg")}
          style={{ maxWidth: 360, marginBottom: 16, width: "100%" }}
          onSearch={(v) => setClientOrgFilter(v)}
          enterButton
        />
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} data-testid="case-list-table-skeleton" />
        ) : isMobile ? (
          cases.length === 0 ? (
            <Empty
              description={
                <Space direction="vertical" size="small">
                  <TypographyEmpty />
                  <Typography.Text type="secondary">{t("compliance.caseList.emptyHint")}</Typography.Text>
                </Space>
              }
            >
              <Space direction="vertical">{demoButtons}</Space>
            </Empty>
          ) : (
            <MobileDataCards
              testId="case-list-mobile"
              items={cases}
              rowKey={(row) => row.id}
              title={(row) => row.supplier_name}
              fields={(row) => [
                {
                  label: t("compliance.columns.commodity"),
                  value: row.commodity.replace("_", " "),
                },
                {
                  label: t("compliance.columns.destination"),
                  value: countryLabel(row.destination_country, i18n.language) || row.destination_country,
                },
                {
                  label: t("compliance.columns.status"),
                  value: <CaseStatusBadge status={row.status} />,
                },
                {
                  label: t("compliance.readiness.short"),
                  value: (
                    <Progress
                      percent={readinessPercentFromStatus(row.status)}
                      size="small"
                      status={row.status === "shared" ? "success" : "active"}
                    />
                  ),
                },
              ]}
              actions={(row) => (
                <Space wrap size="small">
                  <Button
                    type="primary"
                    className="dds-touch-target"
                    onClick={() => navigate(`/dds/cases/${row.id}`)}
                  >
                    {t("common.open")}
                  </Button>
                  {(row.status === "passport_ready" || row.status === "shared") && (
                    <Button
                      className="dds-touch-target"
                      loading={downloadingId === row.id}
                      disabled={downloadingId !== null && downloadingId !== row.id}
                      onClick={async () => {
                        setDownloadingId(row.id);
                        try {
                          await downloadCompliancePassport(row.id);
                        } catch {
                          message.error(t("compliance.passport.downloadFailed"));
                        } finally {
                          setDownloadingId(null);
                        }
                      }}
                    >
                      {t("common.download")}
                    </Button>
                  )}
                </Space>
              )}
            />
          )
        ) : (
          <Table
            rowKey="id"
            loading={false}
            dataSource={cases}
            columns={columns}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <Space direction="vertical" size="small">
                      <TypographyEmpty />
                      <Typography.Text type="secondary">{t("compliance.caseList.emptyHint")}</Typography.Text>
                    </Space>
                  }
                >
                  {demoButtons}
                </Empty>
              ),
            }}
          />
        )}
      </Card>
      <InvestorDemoModal
        open={investorOpen}
        onClose={() => setInvestorOpen(false)}
        onFinished={() => void load()}
      />
    </Space>
  );
}

function TypographyEmpty() {
  const { t } = useTranslation();
  return (
    <>
      <div style={{ fontWeight: 600 }}>{t("compliance.caseList.emptyTitle")}</div>
      <div style={{ color: "rgba(0,0,0,0.65)", maxWidth: 480, margin: "0 auto" }}>
        {t("compliance.caseList.emptyBody")}
      </div>
    </>
  );
}
